import React from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine, LabelList
} from 'recharts';
import { AnalysisResult } from '../types';
import { Box, Typography, Paper, Divider } from '@mui/material';

interface AnalysisChartsProps {
  result: AnalysisResult;
}

const AnalysisCharts: React.FC<AnalysisChartsProps> = ({ result }) => {
  // Format currency values
  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };
  
  // Extract states where nexus was met
  const getNexusStatesByTrigger = () => {
    if (!result.analysis_data) return { salesTrigger: [], countTrigger: [], bothTriggers: [] };
    
    const stateMap: Record<string, any> = {};
    
    // First pass: Find the earliest instance of nexus for each state
    result.analysis_data.forEach(item => {
      if (item['Met Nexus'] === 'Yes') {
        const stateName = item.State;
        const year = item.Year; // Year is already a number, no parseInt needed
        
        if (!stateMap[stateName] || year < stateMap[stateName].Year) { // Remove parseInt here too
          stateMap[stateName] = item;
        }
      }
    });
    
    // Group by trigger type
    const salesTrigger: any[] = [];
    const countTrigger: any[] = [];
    const bothTriggers: any[] = [];
    
    Object.values(stateMap).forEach(state => {
      const metSalesThreshold = state["Threshold - Total Pct"] >= 100;
      const metCountThreshold = state["Threshold - Count Pct"] >= 100;
      
      const chartData = {
        name: state.State,
        totalSales: state["Period Type"]?.toLowerCase().includes("rolling") 
          ? Number((state.total_at_crossing || 0).toFixed(2))
          : Number((state["Customer Data Total"] || 0).toFixed(2)),
        threshold: Number((state["Threshold Amt"] || 0).toFixed(2)),
        transactionCount: state["Customer Data Count"],  // Always use the full count, not just the count at crossing for rolling period states
        countThreshold: state["Threshold Count"] || 0,
        // Calculate how far over threshold (as percentage)
        salesOverage: state["Threshold - Total Pct"] - 100,
        countOverage: state["Threshold - Count Pct"] - 100,
        crossedOn: state["Crossed on"]
      };
      
      if (metSalesThreshold && metCountThreshold) {
        bothTriggers.push(chartData);
      } else if (metSalesThreshold) {
        salesTrigger.push(chartData);
      } else if (metCountThreshold) {
        countTrigger.push(chartData);
      }
    });
    
    // Sort by how far over threshold they are
    salesTrigger.sort((a, b) => b.salesOverage - a.salesOverage);
    countTrigger.sort((a, b) => b.countOverage - a.countOverage);
    bothTriggers.sort((a, b) => (b.salesOverage + b.countOverage) - (a.salesOverage + a.countOverage));
    
    return { salesTrigger, countTrigger, bothTriggers };
  };
  
  const { salesTrigger, countTrigger, bothTriggers } = getNexusStatesByTrigger();
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Nexus Threshold Analysis
      </Typography>
      
      {/* States that triggered by sales amount only */}
      {salesTrigger.length > 0 && (
        <Paper sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            States Where Sales Amount Triggered Nexus
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following states exceeded their sales amount threshold but not their transaction count threshold
          </Typography>
          
          <Box sx={{ height: Math.max(350, salesTrigger.length * 60) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={salesTrigger}
                margin={{ top: 20, right: 30, left: 50, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={formatCurrency}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={50}
                />
                <Tooltip 
                  formatter={(value: any, name: string) => {
                    if (name === 'totalSales') return formatCurrency(value);
                    if (name === 'threshold') return formatCurrency(value);
                    return value;
                  }}
                  labelFormatter={(label) => {
                    // Find the matching entry to get the crossed date
                    const entry = salesTrigger.find(item => item.name === label);
                    return `State: ${label}${entry?.crossedOn ? `\nCrossed on: ${entry.crossedOn}` : ''}`;
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="totalSales" 
                  name="Actual Sales" 
                  fill="#5075d3" 
                  barSize={20}
                >
                  <LabelList 
                    dataKey="totalSales" 
                    position="right" 
                    formatter={(value: number) => formatCurrency(value)} 
                  />
                </Bar>
                <ReferenceLine 
                  x={0} 
                  stroke="#000" 
                />
                {salesTrigger.map((entry, index) => (
                  <ReferenceLine
                    key={`threshold-${index}`}
                    x={entry.threshold}
                    stroke="#ff0000"
                    strokeDasharray="3 3"
                    isFront={true}
                    ifOverflow="extendDomain"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
      
      {/* States that triggered by transaction count only */}
      {countTrigger.length > 0 && (
        <Paper sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            States Where Transaction Count Triggered Nexus
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following states exceeded their transaction count threshold but not their sales amount threshold
          </Typography>
          
          <Box sx={{ height: Math.max(350, countTrigger.length * 60) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={countTrigger}
                margin={{ top: 20, right: 30, left: 50, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={50}
                />
                <Tooltip 
                  formatter={(value: any) => value.toLocaleString()}
                  labelFormatter={(label) => {
                    // Find the matching entry to get the crossed date
                    const entry = countTrigger.find(item => item.name === label);
                    return `State: ${label}${entry?.crossedOn ? `\nCrossed on: ${entry.crossedOn}` : ''}`;
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="transactionCount" 
                  name="Transaction Count" 
                  fill="#4caf50" 
                  barSize={20}
                >
                  <LabelList 
                    dataKey="transactionCount" 
                    position="right" 
                    formatter={(value: number) => value.toLocaleString()} 
                  />
                </Bar>
                <ReferenceLine 
                  x={0} 
                  stroke="#000" 
                />
                {countTrigger.map((entry, index) => (
                  <ReferenceLine
                    key={`threshold-${index}`}
                    x={entry.countThreshold}
                    stroke="#ff0000"
                    strokeDasharray="3 3"
                    isFront={true}
                    ifOverflow="extendDomain"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
      
      {/* States that triggered by both criteria */}
      {bothTriggers.length > 0 && (
        <Paper sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            States Where Both Thresholds Were Triggered
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following states exceeded both their sales amount and transaction count thresholds
          </Typography>
          
          <Divider sx={{ my: 2 }}>
            <Typography variant="subtitle1">Sales Amount</Typography>
          </Divider>
          
          <Box sx={{ height: Math.max(350, bothTriggers.length * 60) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={bothTriggers}
                margin={{ top: 20, right: 30, left: 50, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={formatCurrency}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={50}
                />
                <Tooltip 
                  formatter={(value: any) => formatCurrency(value)}
                  labelFormatter={(label) => {
                    // Find the matching entry to get the crossed date
                    const entry = bothTriggers.find(item => item.name === label);
                    return `State: ${label}${entry?.crossedOn ? `\nCrossed on: ${entry.crossedOn}` : ''}`;
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="totalSales" 
                  name="Actual Sales" 
                  fill="#5075d3" 
                  barSize={20}
                >
                  <LabelList 
                    dataKey="totalSales" 
                    position="right" 
                    formatter={(value: number) => formatCurrency(value)} 
                  />
                </Bar>
                {bothTriggers.map((entry, index) => (
                  <ReferenceLine
                    key={`threshold-${index}`}
                    x={entry.threshold}
                    stroke="#ff0000"
                    strokeDasharray="3 3"
                    isFront={true}
                    ifOverflow="extendDomain"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
          
          <Divider sx={{ my: 2 }}>
            <Typography variant="subtitle1">Transaction Count</Typography>
          </Divider>
          
          <Box sx={{ height: Math.max(350, bothTriggers.length * 60) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={bothTriggers}
                margin={{ top: 20, right: 30, left: 50, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={50}
                />
                <Tooltip 
                  formatter={(value: any) => value.toLocaleString()}
                  labelFormatter={(label) => {
                    // Find the matching entry to get the crossed date
                    const entry = bothTriggers.find(item => item.name === label);
                    return `State: ${label}${entry?.crossedOn ? `\nCrossed on: ${entry.crossedOn}` : ''}`;
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="transactionCount" 
                  name="Transaction Count" 
                  fill="#4caf50" 
                  barSize={20}
                >
                  <LabelList 
                    dataKey="transactionCount" 
                    position="right" 
                    formatter={(value: number) => value.toLocaleString()} 
                  />
                </Bar>
                {bothTriggers.map((entry, index) => (
                  <ReferenceLine
                    key={`threshold-${index}`}
                    x={entry.countThreshold}
                    stroke="#ff0000"
                    strokeDasharray="3 3"
                    isFront={true}
                    ifOverflow="extendDomain"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      )}
      
      {salesTrigger.length === 0 && countTrigger.length === 0 && bothTriggers.length === 0 && (
        <Paper sx={{ p: 2, mb: 4, textAlign: 'center' }}>
          <Typography variant="body1">
            No states have triggered nexus thresholds in the analyzed data.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AnalysisCharts;